import React, { useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { toast } from 'sonner';
import { ImagePlus, Trash2, ZoomIn, ZoomOut } from 'lucide-react';

const MAX_LADO = 1600;
const redondear = (a) => Object.fromEntries(['x', 'y', 'width', 'height'].map(k => [k, Math.round(a[k] * 100) / 100]));

// Reduce la foto en el navegador antes de subirla (más rápida y sin límites del servidor)
const reducirFoto = (archivo) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const escala = Math.min(1, MAX_LADO / Math.max(img.width, img.height));
    const lienzo = Object.assign(document.createElement('canvas'), { width: Math.round(img.width * escala), height: Math.round(img.height * escala) });
    lienzo.getContext('2d').drawImage(img, 0, 0, lienzo.width, lienzo.height);
    URL.revokeObjectURL(img.src);
    lienzo.toBlob(b => (b ? resolve(b) : reject(new Error('sin datos'))), 'image/jpeg', 0.88);
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(archivo);
});

export const useVistaFoto = (foto) => {
  const [vista, setVista] = useState(null);
  useEffect(() => {
    if (!foto.archivo) { setVista(foto.quitar ? null : foto.url); return undefined; }
    const url = URL.createObjectURL(foto.archivo);
    setVista(url);
    return () => URL.revokeObjectURL(url);
  }, [foto]);
  return vista;
};

const Encuadre = ({ vista, encuadre, setEncuadre }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const espera = useRef(null);
  useEffect(() => () => clearTimeout(espera.current), []);
  // Agrupa los cambios mientras se arrastra para no recargar la vista previa en cada movimiento
  const alCambiarArea = (area) => {
    if (!Object.values(area).every(Number.isFinite)) return;
    clearTimeout(espera.current);
    espera.current = setTimeout(() => setEncuadre(redondear(area)), 150);
  };
  return (
    <div className="w-full max-w-md space-y-3" data-testid="boda-foto-encuadre">
      {/* Alto fijo en línea: el recorte necesita medidas reales aunque los estilos aún no hayan cargado */}
      <div className="w-full overflow-hidden rounded-lg bg-slate-800" style={{ position: 'relative', height: 300 }} data-testid="boda-foto-recorte">
        <Cropper image={vista} crop={crop} zoom={zoom} aspect={4 / 5} cropShape="round" showGrid={false} minZoom={1} maxZoom={4} zoomSpeed={0.15}
          initialCroppedAreaPercentages={encuadre || undefined} onCropChange={c => Number.isFinite(c.x) && Number.isFinite(c.y) && setCrop(c)}
          onZoomChange={z => Number.isFinite(z) && setZoom(z)} onCropComplete={area => Object.values(area).every(Number.isFinite) && setEncuadre(redondear(area))}
          onCropAreaChange={alCambiarArea} mediaProps={{ alt: 'Foto de la pareja' }} />
      </div>
      <div className="flex items-center gap-3">
        <ZoomOut className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
        <input type="range" min={1} max={4} step={0.02} value={zoom} onChange={e => setZoom(Number(e.target.value))} aria-label="Zoom de la foto"
          className="w-full cursor-pointer accent-slate-800" style={{ border: 0, boxShadow: 'none', padding: 0, background: 'transparent', height: 20, minHeight: 0 }} data-testid="boda-foto-zoom" />
        <ZoomIn className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
      </div>
      <p className="text-xs text-slate-500">Arrastra la foto y usa el zoom para que los rostros queden dentro del óvalo. Así se verá en la invitación.</p>
    </div>
  );
};

export default function FotoCompartir({ vista, setFoto, encuadre, setEncuadre }) {
  const entrada = useRef(null);

  const elegir = async (e) => {
    const archivo = e.target.files?.[0];
    e.target.value = '';
    if (!archivo) return;
    if (!archivo.type.startsWith('image/')) { toast.error('Elige una foto en JPG, PNG o WEBP'); return; }
    try {
      const reducida = await reducirFoto(archivo);
      setEncuadre(null);
      setFoto(prev => ({ ...prev, archivo: reducida, quitar: false }));
    } catch {
      toast.error('No se pudo leer la foto. Prueba con otra en JPG o PNG');
    }
  };

  const quitar = () => { setEncuadre(null); setFoto(prev => ({ ...prev, archivo: null, quitar: true })); };

  return (
    <div className="space-y-2" data-testid="boda-foto-compartir">
      <p className="text-sm font-medium text-slate-700">Foto de la pareja <span className="font-normal text-slate-500">(opcional)</span></p>
      <p className="text-xs text-slate-500">Aparece en la invitación dentro de un marco con flores y al compartir el enlace por WhatsApp. Si no subes una, la invitación se muestra sin foto y al compartir se ve tu logo.</p>
      {vista && <Encuadre key={vista} vista={vista} encuadre={encuadre} setEncuadre={setEncuadre} />}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => entrada.current?.click()} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50" data-testid="boda-foto-elegir">
          <ImagePlus className="h-4 w-4" />{vista ? 'Cambiar foto' : 'Subir foto'}
        </button>
        {vista && (
          <button type="button" onClick={quitar} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50" data-testid="boda-foto-quitar">
            <Trash2 className="h-4 w-4" />Quitar foto
          </button>
        )}
      </div>
      <input ref={entrada} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={elegir} data-testid="boda-foto-input" />
    </div>
  );
}
